/**
 * Ace multiple-shipment flag — theme-side line item property stamping.
 *
 * Stamps every cart line with a property (default "is_multiple_shipping"):
 *   "false" when at least one location stocks EVERY item in the cart
 *           (the order can ship from a single store),
 *   "true"  when no single location covers the cart (the order must ship
 *           from multiple locations).
 *
 * Why the theme and not the checkout extension: the Storefront API's
 * cartLinesUpdate mutation updates up to 250 lines in ONE request, so even
 * a 100+ item cart is stamped with a single call — no checkout change
 * budget, no TooManyChangesError, no per-line drip.
 *
 * Data sources (all client-side, no app backend):
 *   - /cart.js                  -> cart token + current properties
 *   - Storefront storeAvailability -> locations stocking each variant
 *     (requires a public token with unauthenticated_read_product_pickup_locations)
 *   - Storefront cartLinesUpdate  -> one bulk write for all lines
 *
 * Runs on EVERY page so carts stamped from product-page adds are already
 * correct when a buyer types /checkout directly without visiting the cart
 * page. A localStorage fingerprint (variant+qty+value per line) makes the
 * per-pageview cost one /cart.js read — GraphQL only runs when the cart
 * actually changed. Re-stamps after any cart mutation
 * (/cart/add|change|update|clear via fetch or XHR) and guards checkout
 * buttons/links: if the cart changed since the last stamp, the click waits
 * for a fresh stamp (4s failsafe) before continuing.
 *
 * Known remaining gap: accelerated "Buy it now" buttons (Shop Pay et al.)
 * bypass the cart entirely — the property is absent there and the consumer
 * must treat missing as "false".
 *
 * Configure via window.AceMultipleFlag in snippets/ace-multiple-flag.liquid.
 */
(function () {
  'use strict';

  var cfg = window.AceMultipleFlag || {};
  var TOKEN = String(cfg.token || '');
  var KEY = String(cfg.key || 'is_multiple_shipping');
  var API_VERSION = String(cfg.apiVersion || '2026-04');
  var DEBUG = cfg.debug !== false;
  var ENDPOINT = '/api/' + API_VERSION + '/graphql.json';

  function log() {
    if (DEBUG && window.console) {
      console.log.apply(console, ['[multiple-flag]'].concat([].slice.call(arguments)));
    }
  }
  function logError() {
    if (window.console) {
      console.error.apply(console, ['[multiple-flag]'].concat([].slice.call(arguments)));
    }
  }

  if (!TOKEN) {
    logError('no Storefront token configured — stamping disabled');
    return;
  }

  // ---- Storefront GraphQL ----------------------------------------------

  var CART_QUERY =
    'query CartLines($id: ID!) {' +
    '  cart(id: $id) {' +
    '    id' +
    '    lines(first: 250) {' +
    '      nodes {' +
    '        id' +
    '        attributes { key value }' +
    '        merchandise { ... on ProductVariant { id } }' +
    '      }' +
    '    }' +
    '  }' +
    '}';

  var AVAILABILITY_QUERY =
    'query VariantStoreAvailability($ids: [ID!]!) {' +
    '  nodes(ids: $ids) {' +
    '    ... on ProductVariant {' +
    '      id' +
    '      storeAvailability(first: 20) {' +
    '        nodes { available location { id } }' +
    '      }' +
    '    }' +
    '  }' +
    '}';

  var UPDATE_MUTATION =
    'mutation StampLines($cartId: ID!, $lines: [CartLineUpdateInput!]!) {' +
    '  cartLinesUpdate(cartId: $cartId, lines: $lines) {' +
    '    cart { id }' +
    '    userErrors { field message }' +
    '  }' +
    '}';

  function storefront(query, variables) {
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': TOKEN,
      },
      body: JSON.stringify({ query: query, variables: variables }),
    }).then(function (resp) {
      return resp.json();
    });
  }

  // ---- Stamping ---------------------------------------------------------

  var state = {
    dirty: true,       // cart changed since the last successful stamp
    running: null,     // in-flight stamp promise
  };

  // Fingerprint of the last successfully stamped cart, persisted so page
  // navigations (and other tabs) can skip the GraphQL work entirely when
  // nothing changed. One entry per line: variantId x quantity = flag value.
  var SIG_KEY = 'ace-mf-sig';

  function cartSignature(cart, value) {
    return (cart.items || [])
      .map(function (i) {
        var v =
          value != null ? value : ((i.properties || {})[KEY] || '');
        return i.variant_id + 'x' + i.quantity + '=' + v;
      })
      .join('|');
  }

  function readSig() {
    try { return window.localStorage.getItem(SIG_KEY); } catch (e) { return null; }
  }
  function writeSig(sig) {
    try {
      if (sig == null) window.localStorage.removeItem(SIG_KEY);
      else window.localStorage.setItem(SIG_KEY, sig);
    } catch (e) { /* storage unavailable — skip the optimization */ }
  }

  function computeMultiple(availabilityNodes) {
    // Per-variant sets of location ids where the variant is available.
    // Variants with no data are skipped; the cart is "multiple" when at
    // least two variants have data and no location is common to all.
    var sets = [];
    (availabilityNodes || []).forEach(function (node) {
      if (!node || !node.id) return;
      var locs = ((node.storeAvailability || {}).nodes || [])
        .filter(function (n) { return n && n.available && n.location && n.location.id; })
        .map(function (n) { return n.location.id; });
      if (locs.length) sets.push(locs);
    });
    if (sets.length < 2) return false;
    var common = sets.reduce(function (acc, s) {
      return acc.filter(function (id) { return s.indexOf(id) !== -1; });
    });
    return common.length === 0;
  }

  function stamp() {
    if (state.running) return state.running;

    state.running = (async function () {
      var t0 = Date.now();

      // 1. Theme cart -> token (which is the Storefront cart id suffix).
      var cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); });
      if (!cart || !cart.token || !cart.items || !cart.items.length) {
        state.dirty = false;
        writeSig(null);
        log('cart empty — nothing to stamp');
        return true;
      }
      var cartId = 'gid://shopify/Cart/' + cart.token;

      // 2. Storefront view of the same cart: line ids + current attributes.
      var cartRes = await storefront(CART_QUERY, { id: cartId });
      if (cartRes.errors) logError('cart query errors:', cartRes.errors);
      var lines = (((cartRes.data || {}).cart || {}).lines || {}).nodes || [];
      if (!lines.length) {
        state.dirty = false;
        log('no storefront cart lines — nothing to stamp');
        return true;
      }

      // 3. Availability for every unique variant, one batched request.
      var variantIds = [];
      lines.forEach(function (l) {
        var vid = l && l.merchandise && l.merchandise.id;
        if (vid && variantIds.indexOf(vid) === -1) variantIds.push(vid);
      });
      var availRes = await storefront(AVAILABILITY_QUERY, { ids: variantIds });
      if (availRes.errors) logError('availability query errors:', availRes.errors);
      var multiple = computeMultiple((availRes.data || {}).nodes);
      var want = multiple ? 'true' : 'false';
      log('cart common-location check ->', KEY, '=', want);

      // 4. One bulk update for every line whose value differs. ALL existing
      //    attributes are preserved verbatim by re-sending them alongside
      //    our key (empty values included — verified supported by
      //    cartLinesUpdate on this API version).
      var toUpdate = lines.filter(function (l) {
        var current = null;
        (l.attributes || []).forEach(function (a) {
          if (a && a.key === KEY) current = a.value;
        });
        return current !== want;
      });
      if (!toUpdate.length) {
        state.dirty = false;
        writeSig(cartSignature(cart, want));
        log('all', lines.length, 'lines already up to date (' + (Date.now() - t0) + 'ms)');
        return true;
      }

      var updates = toUpdate.map(function (l) {
        var kept = (l.attributes || [])
          .filter(function (a) { return a && a.key && a.key !== KEY; })
          .map(function (a) {
            return { key: a.key, value: a.value == null ? '' : String(a.value) };
          });
        return {
          id: l.id,
          attributes: kept.concat([{ key: KEY, value: want }]),
        };
      });
      var updateRes = await storefront(UPDATE_MUTATION, { cartId: cartId, lines: updates });
      var errs =
        (updateRes.errors || []).concat(
          (((updateRes.data || {}).cartLinesUpdate || {}).userErrors || [])
        );
      if (errs.length) {
        logError('bulk update errors:', errs);
        return false;
      }

      state.dirty = false;
      writeSig(cartSignature(cart, want));
      log('stamped', updates.length, 'of', lines.length, 'lines in one request (' + (Date.now() - t0) + 'ms)');
      return true;
    })()
      .catch(function (error) {
        logError('stamp failed:', error);
        return false;
      })
      .then(function (ok) {
        state.running = null;
        return ok;
      });

    return state.running;
  }

  var debounceTimer = null;
  function scheduleStamp() {
    state.dirty = true;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      debounceTimer = null;
      stamp();
    }, 600);
  }

  // ---- Watch cart mutations (theme AJAX) --------------------------------
  // dirty is set the moment a mutation REQUEST starts (not when it
  // resolves), so a buyer clicking checkout while /cart/change is still in
  // flight is held. pending tracks in-flight mutations so the hold can wait
  // for them to land before stamping (otherwise the stamp would read the
  // pre-change cart).

  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?/;
  var pendingMutations = 0;

  function mutationStarted() {
    state.dirty = true;
    pendingMutations++;
  }
  function mutationSettled() {
    pendingMutations = Math.max(0, pendingMutations - 1);
    scheduleStamp();
  }

  var origFetch = window.fetch;
  window.fetch = function () {
    var url = arguments[0] && (arguments[0].url || String(arguments[0]));
    var isMutation = url && CART_MUTATION_RE.test(url);
    if (isMutation) mutationStarted();
    var result = origFetch.apply(this, arguments);
    if (isMutation) result.then(mutationSettled, mutationSettled);
    return result;
  };

  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._aceMfCartMutation = !!(url && CART_MUTATION_RE.test(String(url)));
    return origOpen.apply(this, arguments);
  };
  var origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this._aceMfCartMutation) {
      mutationStarted();
      this.addEventListener('loadend', mutationSettled);
    }
    return origSend.apply(this, arguments);
  };

  // ---- Checkout guard ---------------------------------------------------
  // If the cart changed since the last stamp (or a stamp is in flight),
  // hold the checkout navigation until stamping finishes. A 4s failsafe
  // lets the buyer through even if the network hangs — the property is
  // best-effort, checkout must never be blocked for good.

  function needsStamp() {
    return state.dirty || state.running;
  }

  function withStamp(proceed) {
    var done = false;
    var go = function () {
      if (done) return;
      done = true;
      proceed();
    };
    setTimeout(go, 4000);

    // Wait for any in-flight cart mutation to land first, then stamp with
    // the settled cart. The 4s failsafe above bounds the whole sequence.
    (function waitForMutations() {
      if (pendingMutations > 0) {
        setTimeout(waitForMutations, 100);
        return;
      }
      Promise.resolve(stamp()).then(go, go);
    })();
  }

  // Navigate to checkout using whatever checkout form/button is in the
  // LIVE DOM right now. The theme re-renders the cart section after
  // /cart/change, detaching the nodes captured at click time — submitting
  // a detached form is silently canceled by the browser ("form is not
  // connected"), so stale references must never be used. Falls back to a
  // direct /checkout redirect when no connected form exists.
  function goToCheckout() {
    var btn = document.querySelector(
      'button[name="checkout"], input[name="checkout"]'
    );
    var form = btn && btn.isConnected ? btn.form || btn.closest('form') : null;
    if (form && form.isConnected) {
      form.dataset.aceMfBypass = '1';
      if (form.requestSubmit) form.requestSubmit(btn);
      else form.submit();
      return;
    }
    window.location.href = '/checkout';
  }

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || form.dataset.aceMfBypass === '1') return;
      var submitter = event.submitter;
      var isCheckout =
        (submitter && submitter.name === 'checkout') ||
        (form.action && form.action.indexOf('/checkout') !== -1);
      if (!isCheckout || !needsStamp()) return;

      event.preventDefault();
      event.stopPropagation();
      log('checkout held for stamping…');
      withStamp(goToCheckout);
    },
    true
  );

  document.addEventListener(
    'click',
    function (event) {
      if (!event.target || !event.target.closest) return;

      // Direct /checkout links.
      var link = event.target.closest('a[href*="/checkout"]');
      if (link && needsStamp()) {
        event.preventDefault();
        event.stopPropagation();
        log('checkout link held for stamping…');
        var href = link.href;
        withStamp(function () { window.location.href = href; });
        return;
      }

      // The checkout submit button, caught at click time as well — covers
      // theme JS that navigates from its own click handler instead of
      // letting the form submit.
      var btn = event.target.closest('button[name="checkout"], input[name="checkout"]');
      if (btn && needsStamp()) {
        var form = btn.form || btn.closest('form');
        if (!form || form.dataset.aceMfBypass === '1') return;
        event.preventDefault();
        event.stopPropagation();
        log('checkout button held for stamping…');
        withStamp(goToCheckout);
      }
    },
    true
  );

  // ---- Initial run ------------------------------------------------------
  // Cheap on every pageview: one /cart.js read; the full GraphQL work only
  // runs when the cart differs from the last stamped fingerprint.

  function quickCheck() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) {
          state.dirty = false;
          writeSig(null);
          return;
        }
        var sig = cartSignature(cart, null);
        if (sig === readSig()) {
          state.dirty = false;
          log('cart unchanged since last stamp — skipping');
          return;
        }
        stamp();
      })
      .catch(function () { stamp(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', quickCheck);
  } else {
    quickCheck();
  }
})();
